import { z } from 'zod';
import { instrumentoSchema, tendenciaSchema } from '../eventos.js';
import { colorDeReglaSchema, conjuntoDeReglasSchema, entradaDeSemaforoSchema } from './reglas.js';
/**
 * Contrato del motor de reglas hacia la PWA.
 *
 * Dos cosas viajan: la **vista entre hitos** de un paciente y la **configuración
 * vigente** de la institución. Las pantallas que las consumen son de E3.5; el
 * contrato entra ahora para que la vista y el panel se escriban contra la misma
 * definición.
 *
 * Nada de acá lleva PII: identificadores, códigos de listas cerradas, números y
 * fechas. El nombre del paciente vive en el `Patient` de FHIR y no pasa por acá.
 */
export const evaluacionDeLaVistaSchema = z.strictObject({
    instrumento: instrumentoSchema,
    entrada: entradaDeSemaforoSchema,
    valor: z.number(),
    color: colorDeReglaSchema,
    reglaId: z.string(),
    /** La fecha va **siempre** con el color: entre hitos es la mitad del dato. */
    medidoAt: z.iso.datetime(),
    hito: z.string().nullable(),
    tendencia: tendenciaSchema.nullable(),
    /** Si la flecha es buena noticia. En el Katz-15, subir es empeorar. */
    mejora: z.boolean().nullable(),
    vencida: z.boolean(),
});
/**
 * Actividad reciente · **sin color, por definición de Fundación** (06/08/2026):
 * "días desde el último contacto y un ratio de n contactos realizados / n
 * contactos intentados (sin color)". Reemplaza a la "adherencia %" con umbral.
 */
export const actividadRecienteSchema = z.strictObject({
    diasDesdeUltimoContacto: z.number().int().nonnegative().nullable(),
    contactosRealizados: z.number().int().nonnegative(),
    contactosIntentados: z.number().int().nonnegative(),
    ratio: z.number().min(0).max(1).nullable(),
});
export const vistaDelSemaforoSchema = z.strictObject({
    programaId: z.uuid(),
    /** `null` mientras no haya ninguna escala medida. No es verde: es sin color. */
    color: colorDeReglaSchema.nullable(),
    colorDesde: z.iso.datetime().nullable(),
    colorInstrumento: instrumentoSchema.nullable(),
    evaluaciones: z.array(evaluacionDeLaVistaSchema),
    actividad: actividadRecienteSchema,
    /**
     * Estado de contactabilidad. Hoy **siempre `null`**: el gris está apagado hasta
     * que Fundación defina n intentos y ventana. Está en el contrato desde ahora
     * para que encenderlo no cambie la forma de la respuesta.
     */
    contactabilidad: z.literal('gris').nullable(),
    reglaVersion: z.number().int().positive(),
});
/**
 * Publicación de una versión de la configuración.
 *
 * El `version` del conjunto lo pisa el servidor: dejar que lo mande quien publica
 * invita a sobrescribir una versión existente, y una versión pisada es auditoría
 * perdida.
 */
export const publicarReglasSchema = z.strictObject({
    reglas: conjuntoDeReglasSchema,
    /**
     * Por qué se publica. Obligatorio y con largo mínimo: un cambio de umbral
     * clínico sin motivo no se puede auditar seis meses después. La base tiene el
     * mismo CHECK.
     */
    motivo: z.string().min(10).max(300),
});
export const configuracionVigenteSchema = z.strictObject({
    version: z.number().int().positive(),
    origen: z.enum(['tenant', 'defaults']),
    publicadaAt: z.iso.datetime().nullable(),
    reglas: conjuntoDeReglasSchema,
});
export const resultadoDeLaPublicacionSchema = z.strictObject({
    version: z.number().int().positive(),
    versionAnterior: z.number().int().positive().nullable(),
    /** Campos que cambiaron. Es la cantidad de eventos de auditoría emitidos. */
    cambios: z.number().int().nonnegative(),
});
//# sourceMappingURL=api.js.map